import axios from 'axios';
import { getStoredToken, clearStoredTokens } from '@/store/authStore';
import { installOfflineInterceptors } from './offline/axios-interceptor';
import { recordApiCall } from './offline/api-discovery';

const API_URL = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:4000/api';
const OFFLINE_ENABLED = process.env.NEXT_PUBLIC_OFFLINE_MODE === '1';

/// Ventana (ms) durante la cual toleramos fallos 5xx del refresh-token
/// antes de cerrar la sesión y mandar al usuario al login. Sin este
/// timeout, un backend caído dejaba al usuario en estado "zombie":
/// `isAuthenticated: true` pero todas las peticiones devolviendo 401.
const REFRESH_FAILURE_TIMEOUT_MS = 30_000;

/// Timestamp del primer fallo no-auth (5xx/red) del refresh. Se resetea
/// al primer refresh exitoso. Si supera REFRESH_FAILURE_TIMEOUT_MS sin
/// éxito → logout forzado.
let refreshFailureStartedAt: number | null = null;

/// Renovación en curso, compartida: el backend ROTA el refresh en cada
/// renovación y trata el token anterior como robado. Si varias peticiones
/// reciben 401 a la vez (recarga con el access caducado), todas deben esperar
/// a UNA sola renovación; antes cada una pedía la suya con la misma cookie y
/// la segunda tumbaba la sesión entera ("reutilización de token").
let refreshInFlight: Promise<string> | null = null;

/** Renueva el access token (una sola vez aunque lo pidan varias peticiones). */
function refreshAccessToken(legacyRefresh: string | null): Promise<string> {
  if (!refreshInFlight) {
    refreshInFlight = axios
      .post(`${API_URL}/auth/refresh`, {}, {
        withCredentials: true,
        headers: legacyRefresh ? { Authorization: `Bearer ${legacyRefresh}` } : {},
      })
      .then((response) => {
        const { accessToken } = response.data as { accessToken: string };
        // Guardar el access donde viviera el anterior; el refresh ya no se
        // guarda (cookie httpOnly) y el legado del storage se purga.
        const inLocal =
          !!localStorage.getItem('access_token') ||
          !!localStorage.getItem('refresh_token');
        const storage = inLocal ? localStorage : sessionStorage;
        storage.setItem('access_token', accessToken);
        localStorage.removeItem('refresh_token');
        sessionStorage.removeItem('refresh_token');
        return accessToken;
      })
      .finally(() => {
        refreshInFlight = null;
      });
  }
  return refreshInFlight;
}

export const apiClient = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

// Interceptor offline-first. Se aplica solo si la feature está
// activada (env var build-time). Con flag OFF, el código se importa
// pero `installOfflineInterceptors` nunca se llama: axios opera
// exactamente como antes — mismas peticiones, mismas cabeceras, sin
// `Idempotency-Key` ni outbox.
if (OFFLINE_ENABLED) {
  installOfflineInterceptors(apiClient);
}

// Request interceptor to add auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = getStoredToken('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle token refresh
apiClient.interceptors.response.use(
  (response) => {
    // Auto-aprendizaje del warmup offline: cada GET 2xx a /api/* se
    // registra para que el siguiente warmup lo incluya. Así no
    // necesitamos mantener listas hardcodeadas — la app aprende qué
    // endpoints usa de verdad. Defensive: silencia errores del
    // localStorage (private mode, quota llena).
    if (OFFLINE_ENABLED) {
      try {
        const method = (response.config.method ?? 'get').toLowerCase();
        if (method === 'get' && response.status >= 200 && response.status < 300) {
          const url = response.config.url;
          if (typeof url === 'string') recordApiCall(url);
        }
      } catch {
        // No bloquear la respuesta por un fallo del discovery.
      }
    }
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // If 401 and not already retrying, try to refresh token
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        // El refresh vive en la cookie httpOnly `boosters_ai_refresh` (el navegador
        // la adjunta con `withCredentials`); el Bearer del storage es solo la
        // transición de sesiones abiertas antes del cambio. Sin sesión previa
        // (p. ej. 401 de un login fallido) no hay nada que refrescar.
        const legacyRefresh = getStoredToken('refresh_token');
        const hadSession = !!getStoredToken('access_token') || !!legacyRefresh;
        const isAuthRoute = /\/auth\/(login|register|google|refresh|logout|set-password|invite)/.test(
          String(originalRequest.url ?? ''),
        );
        if (hadSession && !isAuthRoute) {
          const accessToken = await refreshAccessToken(legacyRefresh);

          // Refresh exitoso: reseteamos el tracker de fallos 5xx, ya
          // que tenemos un access token válido de nuevo.
          refreshFailureStartedAt = null;

          originalRequest.headers.Authorization = `Bearer ${accessToken}`;
          return apiClient(originalRequest);
        }
      } catch (refreshError) {
        const refreshStatus = (refreshError as { response?: { status?: number } })
          ?.response?.status;
        const isAuthFailure =
          refreshStatus === 401 || refreshStatus === 403;
        if (isAuthFailure) {
          // 401/403 reales: el refresh token es inválido. Tirar sesión.
          refreshFailureStartedAt = null;
          clearStoredTokens();
          window.location.href = '/login';
        } else {
          // 5xx, red, timeout: el backend está caído pero la sesión
          // podría seguir siendo válida cuando vuelva. Conservamos los
          // tokens — el siguiente intento de refresh debería resolver.
          //
          // Pero si los fallos NO-auth persisten durante > 30 s, la app
          // queda en estado zombie (isAuthenticated:true con todas las
          // peticiones devolviendo 401 sin posibilidad de refresh). Al
          // pasar el timeout, forzamos logout para que el usuario tenga
          // un estado claro.
          const now = Date.now();
          if (refreshFailureStartedAt === null) {
            refreshFailureStartedAt = now;
          } else if (now - refreshFailureStartedAt > REFRESH_FAILURE_TIMEOUT_MS) {
             
            console.warn(
              `[axios] Refresh ha fallado durante > ${REFRESH_FAILURE_TIMEOUT_MS}ms ` +
              'sin recuperarse. Logout forzado para evitar sesión zombie.',
            );
            refreshFailureStartedAt = null;
            clearStoredTokens();
            window.location.href = '/login';
          }
        }
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

// Helper para configuración de subida de archivos
export function getUploadConfig() {
  return {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  };
}

export default apiClient;
